/**
 * @file src/config/defaultPair.js
 * @description The orbiter and world anything falls back to, and the copy a download leaves behind.
 *
 * Rung 2 and rung 3 of the one resolution order every surface follows (`userDefaultPair.js` owns the
 * description of all three): the signed-in person's own pair, then the platform's. A file on this
 * machine is the plainest case — it has no author and no track document, so nobody ever paired it —
 * and the assembler reaches here too whenever a track's orbiter has gone away.
 *
 * This lived inside `local/localLibrary.js`, where it was only ever about dropped files. It moved
 * because a DOWNLOAD needs the same answer: with no connection the settings read fails, the platform
 * session cannot be hydrated, and the chain runs all the way down to the in-app fallback constants —
 * whose ids (`orbiter-fallback`, `fallback-world`) are then REQUESTED from the API, fail, and leave a
 * plain sphere where a world should be. That is the "wrong fallback" a local file landed on offline.
 *
 * So the pair is something a download stores, and {@link primeDefaultPair} makes that copy available
 * after the live chain cannot answer. It is not a fourth rung: what was stored IS the answer this
 * order produced when there was a connection, captured at download time.
 */
import { DEFAULT_ORBITER_FALLBACK } from '../defaults/orbiterFallback.js';
import { DEFAULT_ENTANGLED_WORLD_FALLBACK } from '../defaults/entangledWorldFallback.js';
import { Constants } from './Constants.js';
import { hasResolvedUserDefaultPair, resolveUserDefaultPair } from './userDefaultPair.js';
import {
  fetchEntangledWorldRelease,
  fetchOrbiterRelease,
  fetchTrackRelease,
} from '../api/dataManager/loaders.js';
import {
  readLiveDefaultPair,
  readDefaultPairGeneration,
  readPlatformPair,
  readStoredDefaultPair,
  resetDefaultPairState,
  storeLiveDefaultPair,
  storeOfflineDefaultPair,
  storePlatformPair,
} from './defaultPairState.js';

/** The in-app pair. Carries releases, so nothing has to be fetched to use it — see the note above. */
function inAppFallbackPair() {
  return {
    orbiterId: DEFAULT_ORBITER_FALLBACK.orbiterId,
    orbiterVersion: DEFAULT_ORBITER_FALLBACK.release?.version ?? null,
    orbiterRelease: DEFAULT_ORBITER_FALLBACK.release,
    worldId: DEFAULT_ENTANGLED_WORLD_FALLBACK.worldId,
    worldVersion: DEFAULT_ENTANGLED_WORLD_FALLBACK.release?.version ?? null,
    worldRelease: DEFAULT_ENTANGLED_WORLD_FALLBACK.release,
  };
}

/**
 * The platform's own pair: the default play track's orbiter and world when that session can be read,
 * the in-app fallback pair otherwise. Resolved once.
 */
async function resolvePlatformPair(generation) {
  const stored = readPlatformPair();
  if (stored) return { pair: stored, answered: true };
  const defaultTrackId = Constants.DEFAULT_PLAY_TRACK_ID || null;
  if (!defaultTrackId) {
    return { pair: storePlatformPair(inAppFallbackPair(), generation) || inAppFallbackPair(), answered: true };
  }
  try {
    const payload = await fetchTrackRelease(defaultTrackId, { hydrate: 2 });
    const hydration = payload?.hydration || null;
    const orbiterRelease = hydration?.orbiterRelease || null;
    const worldRelease = hydration?.worldRelease || null;
    const orbiterId = hydration?.orbiterId || orbiterRelease?.metadata?.orbiterId || orbiterRelease?.metadata?.id || null;
    const worldId = hydration?.worldId || worldRelease?.metadata?.worldId || worldRelease?.metadata?.id || null;
    if (orbiterRelease && worldRelease && orbiterId && worldId) {
      const resolved = {
        orbiterId,
        orbiterVersion: orbiterRelease.version ?? null,
        orbiterRelease,
        worldId,
        worldVersion: worldRelease.version ?? null,
        worldRelease,
      };
      const pair = storePlatformPair(resolved, generation) || resolved;
      return { pair, answered: true };
    }
  } catch (error) {
    console.info('[defaults] platform default session not readable; using the in-app fallback pair', error?.message || error);
  }
  // Do not remember a network failure as the platform's answer. A later online lookup must retry,
  // and a stored offline pair may be used only while neither live rung could answer.
  return { pair: inAppFallbackPair(), answered: false };
}

async function resolvePreferredOrbiter(orbiterId, offline, platform) {
  if (!orbiterId) return {
    id: platform.orbiterId,
    version: platform.orbiterVersion,
    release: platform.orbiterRelease,
  };
  try {
    const payload = await fetchOrbiterRelease(orbiterId, { version: null, useFallback: false });
    if (payload?.release) {
      return { id: orbiterId, version: payload.release.version ?? null, release: payload.release };
    }
  } catch {
    // A stored release for this same live choice is the offline answer immediately below.
  }
  if (offline?.orbiterId === orbiterId && offline.orbiterRelease) {
    return { id: orbiterId, version: offline.orbiterVersion, release: offline.orbiterRelease };
  }
  return { id: platform.orbiterId, version: platform.orbiterVersion, release: platform.orbiterRelease };
}

async function resolvePreferredWorld(worldId, offline, platform) {
  if (!worldId) return {
    id: platform.worldId,
    version: platform.worldVersion,
    release: platform.worldRelease,
  };
  try {
    const payload = await fetchEntangledWorldRelease(worldId, { version: null });
    if (payload?.release) {
      return { id: worldId, version: payload.release.version ?? null, release: payload.release };
    }
  } catch {
    // A stored release for this same live choice is the offline answer immediately below.
  }
  if (offline?.worldId === worldId && offline.worldRelease) {
    return { id: worldId, version: offline.worldVersion, release: offline.worldRelease };
  }
  return { id: platform.worldId, version: platform.worldVersion, release: platform.worldRelease };
}

/**
 * The pair to use when nothing chose one.
 *
 * The pair always carries both releases. The platform session already hands them over; a user's
 * chosen ids are resolved here. Local files build their synthetic track hydration directly from this
 * object, so returning ids with null releases would make those files fail before the assembler could
 * repair them. The two halves remain independent.
 *
 * A stored pair is considered only after the live settings/platform chain cannot answer. That keeps
 * an offline boot faithful without allowing yesterday's download to override today's online choice.
 */
export async function resolveDefaultPair() {
  const live = readLiveDefaultPair();
  if (live) return live;
  const generation = readDefaultPairGeneration();

  const preferred = await resolveUserDefaultPair();
  const userAnswered = hasResolvedUserDefaultPair();
  const platformResult = await resolvePlatformPair(generation);
  if (generation !== readDefaultPairGeneration()) return resolveDefaultPair();
  const platform = platformResult.pair;
  const offline = readStoredDefaultPair();

  // A stored copy is an offline fallback, not a cache in front of the account. Even a live answer
  // saying "no custom pair" sends that half to the platform. Only a platform half that then cannot
  // be read may come from the stored copy.
  if (!userAnswered && platformResult.answered) {
    return storeLiveDefaultPair({ ...platform, resolvedAt: Date.now() }, generation) || resolveDefaultPair();
  }
  if (!userAnswered && !platformResult.answered) {
    return offline || platform;
  }

  const resolvedAt = Date.now();
  const fallback = platformResult.answered ? platform : (offline || platform);
  const [orbiter, world] = await Promise.all([
    resolvePreferredOrbiter(preferred.orbiterId, offline, fallback),
    resolvePreferredWorld(preferred.worldId, offline, fallback),
  ]);
  if (generation !== readDefaultPairGeneration()) return resolveDefaultPair();
  const resolved = {
    orbiterId: orbiter.id,
    orbiterVersion: orbiter.version,
    orbiterRelease: orbiter.release,
    worldId: world.id,
    worldVersion: world.version,
    worldRelease: world.release,
  };
  return storeLiveDefaultPair({ ...resolved, resolvedAt }, generation) || resolveDefaultPair();
}

/**
 * Make a stored pair available to the chain's offline fallback.
 *
 * Called by the offline boot with what a download kept. Two things are required and both were learned
 * the hard way:
 *
 * **Both ids.** Half an answer leaves the other half walking the chain it cannot complete.
 *
 * **Both RELEASES.** A pair is not just two ids to everything that reads it: a file on this machine
 * builds its synthetic release from `orbiterRelease` and `worldRelease` INLINE
 * (`local/localLibrary.js`). Priming ids with null releases therefore produced a local entry with no
 * hydration at all, and the assembler fell through to fetching the entry's own `local:…` id as a
 * track — a 400, and a file that would not play. With the releases in hand this is shaped exactly
 * like the platform's pair, which is the only shape every caller already handles.
 *
 * @param {{ orbiterId?: string|null, orbiterVersion?: string|number|null, worldId?: string|null, worldVersion?: string|number|null }|null} pair
 * @param {{ orbiterRelease?: object|null, worldRelease?: object|null }} [releases]
 * @returns {boolean} whether it was taken
 */
export function primeDefaultPair(pair, { orbiterRelease = null, worldRelease = null } = {}) {
  const orbiterId = typeof pair?.orbiterId === 'string' && pair.orbiterId.trim() ? pair.orbiterId.trim() : null;
  const worldId = typeof pair?.worldId === 'string' && pair.worldId.trim() ? pair.worldId.trim() : null;
  if (!orbiterId || !worldId) return false;
  // A pair with no releases is worse than no pair: see above. Refused rather than half-installed.
  if (!orbiterRelease || !worldRelease) return false;
  storeOfflineDefaultPair({
    orbiterId,
    orbiterVersion: pair.orbiterVersion ?? null,
    orbiterRelease,
    worldId,
    worldVersion: pair.worldVersion ?? null,
    worldRelease,
    resolvedAt: Number(pair.resolvedAt) || 0,
  });
  return true;
}

/** Forget everything — the tests, and a sign-in that changes who is asking. */
export function resetDefaultPair() {
  resetDefaultPairState();
}
